import { useEffect } from "react";

/**
 * useSeo — lightweight per-page SEO hook.
 * Updates document.title, meta description, meta og:title, meta og:description, twitter:title, twitter:description.
 * No external dependency (react-helmet not installed).
 *
 * @param {object} opts
 * @param {string} opts.title - Page title (≤ 60 chars recommended)
 * @param {string} opts.description - Meta description (≤ 155 chars recommended)
 * @param {string} [opts.canonical] - Optional canonical URL
 */
export default function useSeo({ title, description, canonical }) {
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    // Helper to upsert a meta tag
    const upsert = (selector, attr, value, contentValue) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
      }
      el.setAttribute("content", contentValue);
    };

    if (description) {
      // Trim to 155 chars max for safe SERP rendering
      const desc = description.length > 155 ? description.slice(0, 152) + "…" : description;
      upsert('meta[name="description"]', "name", "description", desc);
      upsert('meta[property="og:description"]', "property", "og:description", desc);
      upsert('meta[name="twitter:description"]', "name", "twitter:description", desc);
    }

    if (title) {
      upsert('meta[property="og:title"]', "property", "og:title", title);
      upsert('meta[name="twitter:title"]', "name", "twitter:title", title);
    }

    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }
  }, [title, description, canonical]);
}
